/**
 * Global item tooltip singleton.
 * Summoned by any panel or the PiP on item hover.
 *
 * @module ui/helpers/tooltipManager
 */

let _el = null;
let _visible = false;

/**
 * Get (or build) the tooltip element inside the given document. When the game
 * is popped out into the Document Picture-in-Picture window, the panels live in
 * that window's document — so the tooltip must be created there too, otherwise
 * it renders in the hidden main page and is never seen. If the owning document
 * changed since last time, we move the element across.
 * @param {Document} [doc]
 */
function ensure(doc = document) {
  if (_el && _el.ownerDocument === doc && doc.contains(_el)) return _el;
  // Drop any stale element living in the wrong document.
  if (_el && _el.parentNode) _el.parentNode.removeChild(_el);
  _el = doc.getElementById('pd-tooltip');
  if (!_el || _el.ownerDocument !== doc) {
    _el = doc.createElement('div');
    _el.id = 'pd-tooltip';
    _el.hidden = true;
  }
  if (!doc.contains(_el)) doc.body.appendChild(_el);
  return _el;
}

export const tooltip = {
  /**
   * Show a tooltip near the cursor.
   * @param {number} x — clientX
   * @param {number} y — clientY
   * @param {object} content
   * @param {string} content.name
   * @param {string[]} [content.affixes]
   * @param {string} [content.icon] — item icon key (assets/sprites/items/icon_<icon>.png)
   * @param {string} [content.rarity] — rarity key, for the header tint
   * @param {string} [content.rarityLabel]
   * @param {string} [content.rarityColor]
   * @param {number} [content.level] — item level, shown in the footer
   * @param {string} [content.locked] — reason the item can't be used, if any
   * @param {string} [content.action] — 'equipar' | 'desequipar', shown as a footer hint
   * @param {string} [content.meta]
   * @param {Document} [doc] — document that owns the hovered element (PiP-aware)
   */
  show(x, y, content, doc = document) {
    const el = ensure(doc);
    const rarityColor = content.rarityColor ?? 'var(--pd-chrome-text)';

    // Header: rarity-tinted band with the item icon + name, like a proper loot card.
    const iconHtml = content.icon
      ? `<img class="pd-tooltip-icon" src="assets/sprites/items/icon_${content.icon}.png" alt="" width="26" height="26" style="image-rendering:pixelated" onerror="this.onerror=null;this.src='assets/sprites/items/icon_sword.png'">`
      : '';
    const headerHtml = `
      <div class="pd-tooltip-header" style="background:${rarityColor}22;border-bottom:1px solid ${rarityColor}66">
        ${iconHtml}
        <div class="pd-tooltip-header-text">
          <div class="pd-tooltip-name" style="color:${rarityColor}">${content.name}</div>
          ${content.rarityLabel ? `<div class="pd-tooltip-rarity" style="color:${rarityColor}">${content.rarityLabel}</div>` : ''}
        </div>
      </div>`;

    const affixHtml = (content.affixes ?? []).length
      ? `<div class="pd-tooltip-section">
           <div class="pd-tooltip-section-title">Atributos</div>
           ${content.affixes.map((a) => `<div class="pd-tooltip-affix">${a}</div>`).join('')}
         </div>`
      : '';

    // Optional upgrade comparison vs the equipped item (green = better).
    const compareHtml = (content.compare && content.compare.length)
      ? `<div class="pd-tooltip-compare">
           <div class="pd-tooltip-compare-title">vs. equipado</div>
           ${content.compare.map((r) => `<div class="pd-tooltip-diff" style="color:${r.positive ? '#46d160' : '#ff6b6b'}">${r.text}</div>`).join('')}
         </div>`
      : '';

    // Footer: level requirement + lock/action hint, like the reference tooltips.
    const footerBits = [];
    if (content.level != null) footerBits.push(`Nv.${content.level}`);
    if (content.locked) footerBits.push(`<span style="color:#ff6b6b">🔒 ${content.locked}</span>`);
    else if (content.action) footerBits.push(`clique para ${content.action}`);
    const footerHtml = footerBits.length
      ? `<div class="pd-tooltip-footer">${footerBits.join(' &middot; ')}</div>`
      : '';

    el.innerHTML = `
      ${headerHtml}
      <div class="pd-tooltip-body">
        ${affixHtml}
        ${compareHtml}
        ${content.meta ? `<div class="pd-tooltip-meta">${content.meta}</div>` : ''}
        ${footerHtml}
      </div>
    `;
    el.hidden = false;
    // Position near cursor, offset slightly so it doesn't overlap
    const offsetX = 14;
    const offsetY = 14;
    let left = x + offsetX;
    let top = y + offsetY;
    // Keep on-screen (use the tooltip's own window — PiP has its own viewport).
    const win = el.ownerDocument.defaultView ?? window;
    const maxX = win.innerWidth - el.offsetWidth - 8;
    const maxY = win.innerHeight - el.offsetHeight - 8;
    if (left > maxX) left = x - el.offsetWidth - offsetX;
    if (top > maxY) top = y - el.offsetHeight - offsetY;
    el.style.left = Math.max(4, left) + 'px';
    el.style.top = Math.max(4, top) + 'px';
    el.classList.add('visible');
    _visible = true;
  },

  hide() {
    if (!_el) return;
    _el.classList.remove('visible');
    _el.hidden = true;
    _visible = false;
  },

  moveTo(x, y) {
    if (!_el || !_visible) return;
    const offsetX = 14;
    const offsetY = 14;
    let left = x + offsetX;
    let top = y + offsetY;
    const win = _el.ownerDocument.defaultView ?? window;
    const maxX = win.innerWidth - _el.offsetWidth - 8;
    const maxY = win.innerHeight - _el.offsetHeight - 8;
    if (left > maxX) left = x - _el.offsetWidth - offsetX;
    if (top > maxY) top = y - _el.offsetHeight - offsetY;
    _el.style.left = Math.max(4, left) + 'px';
    _el.style.top = Math.max(4, top) + 'px';
  },
};
